import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Orb } from "@/components/earnos/Orb";
import { Button, Chip, Input, Textarea, useToast, ProgressBar } from "@/components/earnos/kit";
import { apiFetch } from "@/lib/api";

const GOAL_MAP = { "$500/month": 500, "$1,000/month": 1000, "$3,000/month": 3000, "$5,000/month": 5000, "$10,000+/month": 10000 };

const STEPS = [
  { title: "What do you want your income to become?", sub: "Tell EARNOS your monthly income goal. We'll build a path around your target." },
  { title: "How soon do you want to work toward this goal?", sub: "Choose a timeline that matches your situation. EARNOS adjusts your plan to fit." },
  { title: "How much time can you realistically invest each week?", sub: "Your available time helps EARNOS choose the right income path." },
  { title: "What resources do you already have?", sub: "Select everything you have access to." },
  { title: "What type of income path fits you best?", sub: "EARNOS will prioritize opportunities that match your lifestyle." },
  { title: "What skills or experience do you already have?", sub: "Starting from zero is fine — EARNOS can build a path from where you are." },
  { title: "What should EARNOS consider when building your plan?", sub: "Everyone's situation is different. Tell us what works best for you." },
];

const OPTIONS = {
  goal: ["$500/month", "$1,000/month", "$3,000/month", "$5,000/month", "$10,000+/month", "Custom amount"],
  timeline: ["30 days", "60 days", "90 days", "6 months", "Long-term growth"],
  time: ["Less than 5 hours/week", "5–10 hours/week", "10–20 hours/week", "20–40 hours/week", "Full-time focus"],
  resources: ["Smartphone", "Computer/laptop", "Reliable internet", "Vehicle", "Camera/equipment", "Social media audience", "Existing business", "Money to invest", "Skills or experience", "None yet"],
  work: ["Online business", "Helping businesses grow", "Creating content", "Selling products", "Freelancing/services", "Local services", "Technology/AI", "Building a brand", "I'm open to anything"],
  skills: ["Sales", "Customer service", "Writing", "Design", "Video/content creation", "Organization", "Teaching/coaching", "Technology", "Marketing", "Social media", "Hands-on work", "None yet"],
  preferences: ["I don't want phone calls", "I prefer working from home", "I don't want to show my face online", "I have limited money to start", "I need results quickly", "I want something scalable", "I want to build long-term wealth", "I want AI to automate repetitive tasks", "I prefer working alone", "I enjoy working with people"],
};


export default function Onboarding() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [building, setBuilding] = useState(false);
  const [buildMessage, setBuildMessage] = useState("Saving your preferences…");

  const [goal, setGoal] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [timeline, setTimeline] = useState("");
  const [time, setTime] = useState("");
  const [resources, setResources] = useState([]);
  const [work, setWork] = useState([]);
  const [skills, setSkills] = useState([]);
  const [skillsText, setSkillsText] = useState("");
  const [preferences, setPreferences] = useState([]);
  const [success, setSuccess] = useState("");

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const canContinue = () => {
    switch (step) {
      case 0: return !!goal && (goal !== "Custom amount" || !!customAmount);
      case 1: return !!timeline;
      case 2: return !!time;
      case 3: return resources.length > 0;
      case 4: return work.length > 0;
      case 5: return skills.length > 0 || skillsText.trim().length > 0;
      case 6: return success.trim().length > 0;
      default: return true;
    }
  };

  const finish = async () => {
    setBuilding(true);
    setBuildMessage("Saving your preferences…");
    const goalAmount = goal === "Custom amount" ? parseFloat(customAmount) || 0 : GOAL_MAP[goal] || 0;
    const skillList = [...skills];
    if (skillsText.trim()) skillList.push(skillsText.trim());
    const hasMoney = resources.includes("Money to invest");
    const limitedMoney = preferences.includes("I have limited money to start");
    const budget = hasMoney && !limitedMoney ? "Some budget available to invest" : "$0 to start";
    try {
      await apiFetch("/assessment", {
        method: "POST",
        body: {
          income_goal: goal === "Custom amount" ? `$${customAmount}/month` : goal,
          goal_amount: goalAmount,
          deadline: timeline,
          time_per_week: time,
          budget,
          resources,
          work_types: work,
          communication: [],
          skills: skillList.join(", "),
          constraints: preferences,
          success_definition: success,
        },
      });
      setBuildMessage("Requesting your income plan…");
      await apiFetch("/engine/generate", { method: "POST" });
      setBuildMessage("Your income plan is being generated. Waiting for the saved result…");
      const deadline = Date.now() + 180000;
      while (true) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await apiFetch("/engine/status");
        if (s.status === "done") break;
        if (s.status === "error") throw new Error(s.error || "Could not build your Engine. Please try again.");
        if (Date.now() > deadline) throw new Error("This is taking longer than expected. Please try again.");
      }
      setBuildMessage("Loading your saved plan…");
      await refresh();
      navigate("/engine", { replace: true });
    } catch (e) {
      toast.show(e?.message || "Could not build your Engine. Please try again.", "error");
      setBuilding(false);
    }
  };

  if (building) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-6">
        <Orb state="working" size={150} />
        <h2 className="text-xl font-bold text-ink mt-8 text-center">Building Your Income Engine…</h2>
        <p role="status" aria-live="polite" className="mt-6 text-base text-ink-3 text-center max-w-md">{buildMessage}</p>
      </div>
    );
  }

  const s = STEPS[step];
  const progress = (step + 1) / STEPS.length;

  return (
    <div className="min-h-screen bg-surface">
      <div className="max-w-2xl mx-auto px-5 pt-6 pb-32">
        <div className="flex items-center gap-4">
          <button onClick={() => (step === 0 ? navigate(-1) : setStep(step - 1))} data-testid="onboarding-back" className="text-ink">
            <ChevronLeft size={26} />
          </button>
          <ProgressBar value={progress} className="flex-1" />
          <span className="text-sm font-semibold text-ink-2">{step + 1}/{STEPS.length}</span>
        </div>

        <div key={step} className="animate-fade-in mt-8">
          <div className="earnos-kicker mb-2">Question {step + 1} of {STEPS.length}</div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink leading-tight">{s.title}</h1>
          <p className="text-base text-ink-2 mt-2 mb-6 leading-relaxed">{s.sub}</p>

          {step === 0 && (
            <>
              <div className="flex flex-wrap gap-2">
                {OPTIONS.goal.map((o) => <Chip key={o} label={o} selected={goal === o} onClick={() => setGoal(o)} testID={`goal-${o}`} />)}
              </div>
              {goal === "Custom amount" && (
                <Input label="Custom monthly amount ($)" placeholder="e.g. 2500" type="number" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} className="mt-5" testID="custom-amount-input" />
              )}
            </>
          )}
          {step === 1 && <div className="flex flex-wrap gap-2">{OPTIONS.timeline.map((o) => <Chip key={o} label={o} selected={timeline === o} onClick={() => setTimeline(o)} testID={`timeline-${o}`} />)}</div>}
          {step === 2 && <div className="flex flex-wrap gap-2">{OPTIONS.time.map((o) => <Chip key={o} label={o} selected={time === o} onClick={() => setTime(o)} testID={`time-${o}`} />)}</div>}
          {step === 3 && <div className="flex flex-wrap gap-2">{OPTIONS.resources.map((o) => <Chip key={o} label={o} selected={resources.includes(o)} onClick={() => toggle(resources, setResources, o)} />)}</div>}
          {step === 4 && <div className="flex flex-wrap gap-2">{OPTIONS.work.map((o) => <Chip key={o} label={o} selected={work.includes(o)} onClick={() => toggle(work, setWork, o)} />)}</div>}
          {step === 5 && (
            <>
              <div className="flex flex-wrap gap-2">{OPTIONS.skills.map((o) => <Chip key={o} label={o} selected={skills.includes(o)} onClick={() => toggle(skills, setSkills, o)} />)}</div>
              <label className="block text-xs font-bold uppercase tracking-wide text-ink-2 mt-6 mb-3">Tell EARNOS anything else about yourself</label>
              <Textarea placeholder="Share your background, interests, or context..." value={skillsText} onChange={(e) => setSkillsText(e.target.value)} rows={3} testID="skills-text-input" />
            </>
          )}
          {step === 6 && (
            <>
              <div className="flex flex-wrap gap-2">{OPTIONS.preferences.map((o) => <Chip key={o} label={o} selected={preferences.includes(o)} onClick={() => toggle(preferences, setPreferences, o)} />)}</div>
              <label className="block text-xs font-bold uppercase tracking-wide text-ink-2 mt-6 mb-3">What would make EARNOS feel successful for you?</label>
              <Textarea placeholder="e.g. Replacing my job income, having more freedom..." value={success} onChange={(e) => setSuccess(e.target.value)} rows={4} testID="success-input" />
            </>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 border-t border-line bg-surface/95 backdrop-blur p-4">
        <div className="max-w-2xl mx-auto">
          <Button
            title={step === STEPS.length - 1 ? "Create My Income Engine" : "Continue"}
            onClick={() => (step === STEPS.length - 1 ? finish() : setStep(step + 1))}
            disabled={!canContinue()}
            full
            testID="onboarding-continue"
          />
        </div>
      </div>
    </div>
  );
}
